import React, { useContext, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import { UserContext } from './UserContext';
import { updateSubscription } from './apiService';
import {
  CheckGlyph,
  checkBulletSx,
  eyebrowSx,
  pageHeadlineSx,
  sectionCardSx,
  subtleCopySx,
} from './designSystem';
import { FONT_DISPLAY } from './theme';

const PLAN_DEFINITIONS = [
  {
    tier: 'basic',
    label: 'Pilot',
    badge: 'Fastest start',
    description: 'For one school pilot or a tightly scoped evaluation.',
    price: 'Entry rollout',
    capabilities: [
      'Core policy dashboard and profile management',
      'Allowlist, blocklist, and AI review rules',
      'Best for early validation and proof-of-value deployments',
    ],
  },
  {
    tier: 'focused',
    label: 'School',
    badge: 'Recommended',
    description: 'For one school moving from pilot into normal daily use.',
    price: 'School rollout',
    capabilities: [
      'Designed for daily classroom use and staff workflows',
      'Reporting support for principal and IT reviews',
      'Good fit for managed pilots heading toward purchase',
    ],
  },
  {
    tier: 'pro',
    label: 'District',
    badge: 'Broadest rollout',
    description: 'For district or network-wide deployment with broader governance needs.',
    price: 'District rollout',
    capabilities: [
      'Best fit for larger IT teams and multi-school rollouts',
      'Supports broader governance and reporting expectations',
      'Built for shared operational ownership across schools',
    ],
  },
];

function SubscriptionManagement() {
  const { user, setUser } = useContext(UserContext);
  const [loadingTier, setLoadingTier] = useState('');
  const [status, setStatus] = useState(null);

  const currentTier = user?.subscription?.name || 'basic';

  const handlePlanSelect = async (tier) => {
    try {
      setLoadingTier(tier);
      setStatus(null);
      const updatedUser = await updateSubscription(tier);
      setUser(updatedUser);
      setStatus({
        severity: 'success',
        text: `Workspace plan updated to ${PLAN_DEFINITIONS.find((plan) => plan.tier === tier)?.label || tier}.`,
      });
    } catch (error) {
      setStatus({
        severity: 'error',
        text: error.response?.data?.detail || 'Unable to update the workspace plan right now.',
      });
    } finally {
      setLoadingTier('');
    }
  };

  return (
    <Container sx={{ py: { xs: 5, md: 8 } }}>
      <Stack spacing={4}>
        <Box sx={{ maxWidth: 760, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="overline" sx={eyebrowSx}>
            School Packaging
          </Typography>
          <Typography variant="h2" sx={{ ...pageHeadlineSx, mt: 1.5 }}>
            Pick the package that matches your rollout shape.
          </Typography>
          <Typography sx={{ ...subtleCopySx, mt: 2, fontSize: '1.05rem' }}>
            Every package includes the full policy engine — they scale with how widely you deploy it.
          </Typography>
        </Box>

        {status && <Alert severity={status.severity}>{status.text}</Alert>}

        {user && (
          <Card sx={{ ...sectionCardSx, maxWidth: 560, mx: 'auto', width: '100%' }}>
            <CardContent sx={{ p: { xs: 2.5, md: 3 }, '&:last-child': { pb: { xs: 2.5, md: 3 } } }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '12px',
                    display: 'grid',
                    placeItems: 'center',
                    bgcolor: 'rgba(19,148,127,0.1)',
                    color: '#13947F',
                    flexShrink: 0,
                  }}
                >
                  <SchoolOutlinedIcon />
                </Box>
                <Box>
                  <Typography sx={{ color: '#52677A', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                    Current package
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#0B2E43', mt: 0.25 }}>
                    {PLAN_DEFINITIONS.find((plan) => plan.tier === currentTier)?.label || currentTier}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        <Grid container spacing={3} alignItems="stretch">
          {PLAN_DEFINITIONS.map((plan) => {
            const selected = currentTier === plan.tier;
            const featured = plan.tier === 'focused';
            return (
              <Grid item xs={12} md={4} key={plan.tier}>
                <Card
                  sx={{
                    ...sectionCardSx,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    border: featured
                      ? '2px solid rgba(19,148,127,0.55)'
                      : selected
                        ? '2px solid rgba(19,148,127,0.35)'
                        : '1px solid #DFE8EE',
                    boxShadow: featured
                      ? '0 2px 4px rgba(11,46,67,0.05), 0 32px 64px -24px rgba(11,46,67,0.25)'
                      : undefined,
                    transform: { md: featured ? 'translateY(-10px)' : 'none' },
                    transition: 'transform 220ms ease, box-shadow 220ms ease',
                    '&:hover': {
                      transform: { md: featured ? 'translateY(-13px)' : 'translateY(-3px)' },
                    },
                  }}
                >
                  {featured && (
                    <Box
                      sx={{
                        background:
                          'radial-gradient(circle at 85% -20%, rgba(19,148,127,0.45), transparent 50%), linear-gradient(150deg, #0B2E43 0%, #10405D 100%)',
                        p: 3,
                      }}
                    >
                      <Chip label={plan.badge} size="small" sx={{ bgcolor: 'rgba(126,232,214,0.16)', color: '#7EE8D6', fontWeight: 800, mb: 1.5 }} />
                      <Typography variant="h4" sx={{ color: 'white' }}>{plan.label}</Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.62)', mt: 0.5, fontSize: '0.9rem', fontWeight: 600 }}>{plan.price}</Typography>
                    </Box>
                  )}
                  <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {!featured && (
                      <>
                        <Chip
                          label={plan.badge}
                          size="small"
                          sx={{ bgcolor: 'rgba(11,46,67,0.07)', color: '#0B2E43', fontWeight: 800, alignSelf: 'flex-start' }}
                        />
                        <Typography variant="h4" sx={{ mt: 2, color: '#0B2E43' }}>{plan.label}</Typography>
                        <Typography sx={{ color: '#7A8FA0', mt: 0.25, fontSize: '0.9rem', fontWeight: 600 }}>{plan.price}</Typography>
                      </>
                    )}
                    <Typography sx={{ ...subtleCopySx, mt: featured ? 0.5 : 1.5 }}>{plan.description}</Typography>
                    <Stack spacing={1.5} sx={{ mt: 2.5, flex: 1 }}>
                      {plan.capabilities.map((capability) => (
                        <Stack direction="row" spacing={1.25} alignItems="flex-start" key={capability}>
                          <Box sx={checkBulletSx}>
                            <CheckGlyph />
                          </Box>
                          <Typography sx={{ color: '#52677A', fontSize: '0.94rem', lineHeight: 1.6 }}>{capability}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Button
                      variant={featured || !selected ? 'contained' : 'outlined'}
                      color={featured ? 'secondary' : 'primary'}
                      sx={{ mt: 3, fontFamily: FONT_DISPLAY }}
                      fullWidth
                      size="large"
                      disabled={loadingTier === plan.tier || selected}
                      onClick={() => handlePlanSelect(plan.tier)}
                    >
                      {selected ? 'Current Plan' : loadingTier === plan.tier ? 'Updating...' : `Switch to ${plan.label}`}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Stack>
    </Container>
  );
}

export default SubscriptionManagement;
